// AbacatePay Service
// Service for handling all AbacatePay API interactions

import { supabase } from '../lib/supabase';
import { abacatePayConfig } from '../lib/abacatepay-config';

// Types based on AbacatePay SDK and our requirements
export interface CreatePixPaymentRequest {
  participationId: string; // ID from participations table
  amount: number; // Amount in cents
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  customerTaxId?: string;
  description: string;
  externalId: string; // Unique identifier for the payment
}

export interface AbacatePaymentResponse {
  billingId: string;
  paymentUrl: string;
  pixCode: string;
  qrCodeImage?: string;
  amount: number;
  expiresAt: Date;
  status: string;
}

export interface WebhookPayload {
  event: string;
  data: {
    billing: {
      id: string;
      status: 'PAID' | 'CANCELLED' | 'EXPIRED' | 'PENDING';
      amount: number;
      customer: {
        id: string;
        name: string;
        email: string;
      };
    };
    payment?: {
      id: string;
      method: string;
      amount: number;
      fee: number;
    };
  };
  devMode: boolean;
}

export class AbacatePayService {
  private isEnabled: boolean;

  constructor() {
    this.isEnabled = abacatePayConfig.isEnabled();
    
    if (!this.isEnabled) {
      console.warn('AbacatePayService: Integration is disabled via configuration');
    }
  }

  /**
   * Check if AbacatePay integration is enabled and properly configured
   */
  public isActive(): boolean {
    return this.isEnabled;
  }

  /**
   * Create a new PIX payment via AbacatePay
   */
  public async createPixPayment(request: CreatePixPaymentRequest): Promise<AbacatePaymentResponse> {
    if (!this.isActive()) {
      throw new Error('AbacatePay integration is not enabled or properly configured');
    }

    try {
      // Use Supabase Edge Function to create payment (avoids CORS issues)
      const { data, error } = await supabase.functions.invoke('create-pix-payment', {
        body: request
      });

      if (error) {
        console.error('Edge Function error:', error);
        throw new Error(`Failed to create payment: ${error.message}`);
      }

      if (!data) {
        throw new Error('No data returned from payment creation');
      }

      console.log('Edge Function response data:', JSON.stringify(data, null, 2));

      // Validate required fields
      if (!data.billingId) {
        console.error('Missing billingId in response:', data);
        throw new Error('Invalid payment response: missing billingId');
      }

      // Return the payment response
      return {
        billingId: data.billingId,
        paymentUrl: data.paymentUrl || '',
        pixCode: data.pixCode || '',
        qrCodeImage: data.qrCodeImage || null,
        amount: request.amount,
        expiresAt: new Date(data.expiresAt),
        status: data.status || 'created'
      };

    } catch (error) {
      console.error('Error creating PIX payment:', error);
      throw new Error(`Failed to create PIX payment: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get payment status from AbacatePay
   */
  public async getPaymentStatus(billingId: string): Promise<any> {
    if (!this.isActive()) {
      throw new Error('AbacatePay integration is not enabled');
    }

    try {
      // Use Edge Function to check status
      const { data, error } = await supabase.functions.invoke('check-payment-status', {
        body: { paymentId: billingId }
      });

      if (error) {
        console.error('Edge Function error:', error);
        throw new Error(`Failed to check payment status: ${error.message}`);
      }

      if (!data) {
        throw new Error('No data returned from status check');
      }

      console.log('Payment status response:', data);

      return data;

    } catch (error) {
      console.error('Error getting payment status:', error);
      throw new Error(`Failed to get payment status: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Process webhook notification from AbacatePay
   * Note: This is now handled by the Supabase Edge Function
   * This method is kept for reference and testing purposes
   */
  public async processWebhook(payload: WebhookPayload): Promise<boolean> {
    try {
      // Validate webhook is from correct environment
      const expectedDevMode = abacatePayConfig.isDevelopment();
      if (payload.devMode !== expectedDevMode) {
        console.warn(`Webhook environment mismatch: received devMode=${payload.devMode}, expected=${expectedDevMode}`);
      }

      const billingId = payload.data.billing.id;
      const newStatus = this.mapAbacateStatusToLocal(payload.data.billing.status);

      // Update payment status in our database
      const { error: updateError } = await supabase
        .from('abacate_payments')
        .update({
          status: newStatus,
          abacate_payment_id: payload.data.payment?.id,
          paid_at: newStatus === 'paid' ? new Date().toISOString() : null,
          webhook_data: payload,
          last_webhook_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('abacate_billing_id', billingId);

      if (updateError) {
        console.error('Error updating payment status:', updateError);
        return false;
      }

      // If payment was successful, update the participation status
      if (newStatus === 'paid') {
        await this.updateParticipationStatus(billingId, 'paid');
      }

      console.log(`Webhook processed successfully for billing ${billingId}: ${payload.data.billing.status} -> ${newStatus}`);
      return true;

    } catch (error) {
      console.error('Error processing webhook:', error);
      return false;
    }
  }

  /**
   * Validate webhook signature (if AbacatePay provides signature validation)
   */
  public validateWebhookSignature(payload: string, signature: string): boolean {
    // Implementation depends on AbacatePay's webhook signing method
    // For now, return true - should be implemented based on AbacatePay docs
    const secret = abacatePayConfig.getWebhookSecret();
    
    // TODO: Implement actual signature validation
    // Example: crypto.createHmac('sha256', secret).update(payload).digest('hex') === signature
    
    return true; // Placeholder
  }

  /**
   * Save payment record to database
   * Note: This is now handled by the Edge Function
   */
  private async savePaymentRecord(data: {
    participationId: string;
    billingId: string;
    amount: number;
    pixCode: string;
    expiresAt: Date;
    environment: string;
  }): Promise<void> {
    // This method is kept for backward compatibility
    // The actual saving is done in the Edge Function
    console.log('Payment record will be saved by Edge Function');
  }

  /**
   * Update participation status when payment is confirmed
   */
  private async updateParticipationStatus(billingId: string, status: string): Promise<void> {
    // First get the participation ID from the payment record
    const { data: payment, error: paymentError } = await supabase
      .from('abacate_payments')
      .select('participation_id')
      .eq('abacate_billing_id', billingId)
      .single();

    if (paymentError || !payment) {
      console.error('Error finding participation for payment:', paymentError);
      return;
    }

    // Update the participation status
    const { error: updateError } = await supabase
      .from('participations')
      .update({
        status: status,
        paid_at: new Date().toISOString()
      })
      .eq('id', payment.participation_id);

    if (updateError) {
      console.error('Error updating participation status:', updateError);
    }
  }

  /**
   * Map AbacatePay status to our local status format
   */
  private mapAbacateStatusToLocal(abacateStatus: string): string {
    const statusMap: Record<string, string> = {
      'PAID': 'paid',
      'CANCELLED': 'cancelled',
      'EXPIRED': 'expired',
      'PENDING': 'created'
    };

    return statusMap[abacateStatus] || 'error';
  }

  /**
   * Get the Edge Function webhook URL
   */
  public getWebhookUrl(): string {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const webhookSecret = abacatePayConfig.getWebhookSecret();
    
    if (!supabaseUrl) {
      console.error('VITE_SUPABASE_URL not configured');
      return '';
    }
    
    if (!webhookSecret) {
      console.error('Webhook secret not configured');
      return '';
    }
    
    // Extract project ref from Supabase URL
    // Format: https://[PROJECT_REF].supabase.co
    const projectRef = supabaseUrl.split('//')[1]?.split('.')[0];
    
    if (!projectRef) {
      console.error('Could not extract project ref from Supabase URL');
      return '';
    }
    
    // Edge Function URL format with webhook secret as query parameter (as per AbacatePay docs)
    // Production: https://[PROJECT_REF].supabase.co/functions/v1/abacatepay-webhook?webhookSecret=xxx
    // Local: http://localhost:54321/functions/v1/abacatepay-webhook?webhookSecret=xxx
    const environment = abacatePayConfig.getEnvironment();
    const baseUrl = environment === 'development' && supabaseUrl.includes('localhost')
      ? 'http://localhost:54321/functions/v1/abacatepay-webhook'
      : `https://${projectRef}.supabase.co/functions/v1/abacatepay-webhook`;
    
    return `${baseUrl}?webhookSecret=${encodeURIComponent(webhookSecret)}`;
  }

  /**
   * Get debug information
   */
  public getDebugInfo(): object {
    return {
      isEnabled: this.isEnabled,
      isActive: this.isActive(),
      config: abacatePayConfig.getDebugInfo(),
      webhookUrl: this.getWebhookUrl()
    };
  }
}

// Export singleton instance
export const abacatePayService = new AbacatePayService();